import { Tooltip } from '@mantine/core';
import { Icon, variants } from '@uag/react-core';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

import { ArticleMetricsModelPpmCode } from 'api/models';
import { usePlacedArticleMetrics } from 'app/modules/wallforming/components/content-container/hooks/usePlacedArticleMetrics';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { PRIMARY_COLOR_ACCENT_HEX } from 'shared/constants';

const ppmWarningCodes = new Set<string>([
    ArticleMetricsModelPpmCode.E,
    ArticleMetricsModelPpmCode.N,
    ArticleMetricsModelPpmCode.NA
]);

const formatInt = (value: number): string => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);

const formatOneDecimal = (value: number): string =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);

export const ArticleMetricsInfo = () => {
    const articles = usePlacementStore(useShallow((state) => state.articles));
    const { metricsByArticleNumber } = usePlacedArticleMetrics();

    const summary = useMemo(() => {
        let totalPrice = 0;
        let totalCo2 = 0;
        let badPpmArticles = 0;

        Object.values(articles).forEach((article) => {
            const instancesCount = Object.keys(article.articleInstances).length;
            if (instancesCount === 0) {
                return;
            }

            const metrics = metricsByArticleNumber.get(article.articleNumber);
            if (!metrics) {
                return;
            }

            totalPrice += metrics.price * instancesCount;
            totalCo2 += metrics.co2KgEquivalent * instancesCount;

            if (metrics.ppmCode && ppmWarningCodes.has(metrics.ppmCode)) {
                badPpmArticles += 1;
            }
        });

        return {
            totalPrice,
            totalCo2,
            badPpmArticles
        };
    }, [articles, metricsByArticleNumber]);

    return (
        <div className="flex flex-row items-stretch justify-start text-xs">
            <div className="flex shrink-0 items-center justify-center border-r border-border-ultra-light px-3 sm:px-2 w-24">
                <Tooltip label="Overall price of placed article instances" position="bottom" variant={variants.tooltip.bottom}>
                    <div className="flex items-center justify-between w-full h-full">
                        <Icon className="icon-medium" color={PRIMARY_COLOR_ACCENT_HEX} icon="paid" />
                        <div className="w-12 text-right leading-none tabular-nums">
                            {formatInt(summary.totalPrice)}
                        </div>
                    </div>
                </Tooltip>
            </div>

            <div className="flex shrink-0 items-center justify-center border-r border-border-ultra-light px-3 sm:px-2 w-28">
                <Tooltip label="Overall CO2 footprint of placed article instances" position="bottom" variant={variants.tooltip.bottom}>
                    <div className="flex items-center justify-between w-full h-full">
                        <Icon className="icon-medium" color="#0f766e" icon="eco" />
                        <div className="w-18 text-right leading-none tabular-nums">
                            {formatOneDecimal(summary.totalCo2)} kg
                        </div>
                    </div>
                </Tooltip>
            </div>

            <div className="flex shrink-0 items-center justify-center border-r border-border-ultra-light px-3 sm:px-2 w-24">
                <Tooltip label="PPM warning count for articles with status E, N, or NA" position="bottom" variant={variants.tooltip.bottom}>
                    <div className="flex items-center justify-between w-full h-full">
                        <Icon className="icon-medium" color="#d97706" icon="warning" />
                        <div className="leading-none tabular-nums">PPM: {summary.badPpmArticles}</div>
                    </div>
                </Tooltip>
            </div>
        </div>
    );
};
